import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import AdminLayout from '@/components/feature/AdminLayout';
import AdminHeader from '@/components/feature/AdminHeader';
import ModuleGate from '@/components/base/ModuleGate';
import { requireAuth } from '@/middleware/authGuard';
import { supabase } from '@/lib/supabaseClient';
import { getTenantId } from '@/lib/getTenant';
import { createNotification } from '@/lib/createNotification';
import { createReminder, hoursBefore } from '@/lib/createReminder';
import AppointmentModal, {
  type Appointment,
  type ClientOption,
  type LeadOption,
  type ContractOption,
  type JobOption,
} from '@/pages/admin/appointments/components/AppointmentModal';
import type { Conversation, Message } from './types';
import ConversationListPanel from './components/ConversationListPanel';
import ConversationDetail from './components/ConversationDetail';
import LinkClientModal from './components/LinkClientModal';

/* ─── Types ──────────────────────────────────────────────────── */
interface ClientFull {
  id:        string;
  full_name: string;
  email:     string | null;
  phone:     string | null;
}

export default function AiConversationsPage() {
  const navigate = useNavigate();
  const [tenantId, setTenantId]             = useState<string | null>(null);
  const [conversations, setConversations]   = useState<Conversation[]>([]);
  const [messages, setMessages]             = useState<Message[]>([]);
  const [selected, setSelected]             = useState<Conversation | null>(null);
  const [filter, setFilter]                 = useState<'all' | 'open' | 'closed'>('all');
  const [loading, setLoading]               = useState(true);
  const [loadingMsgs, setLoadingMsgs]       = useState(false);
  const [converting, setConverting]         = useState(false);

  /* ── Relational data (lazy loaded) ── */
  const [clients, setClients]   = useState<ClientFull[]>([]);
  const [leads, setLeads]       = useState<LeadOption[]>([]);
  const [contracts, setContracts] = useState<ContractOption[]>([]);
  const [jobs, setJobs]         = useState<JobOption[]>([]);
  const [relLoaded, setRelLoaded] = useState(false);
  const [relLoading, setRelLoading] = useState(false);

  /* ── Modals ── */
  const [linkModalOpen, setLinkModalOpen]       = useState(false);
  const [apptModalOpen, setApptModalOpen]       = useState(false);
  const [linkedClient, setLinkedClient]         = useState<{ id: string; full_name: string } | null>(null);

  /* ── Fetch conversations ── */
  const fetchConversations = useCallback(async (tid: string, f: 'all' | 'open' | 'closed') => {
    setLoading(true);
    let q = supabase
      .from('ai_conversations')
      .select('*')
      .eq('tenant_id', tid)
      .order('created_at', { ascending: false });
    if (f !== 'all') q = q.eq('status', f);
    const { data } = await q;
    setConversations((data as Conversation[]) ?? []);
    setLoading(false);
  }, []);

  /* ── Init ── */
  useEffect(() => {
    const init = async () => {
      const isAuth = await requireAuth();
      if (!isAuth) { navigate('/login'); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid) fetchConversations(tid, 'all');
    };
    init();
  }, [navigate, fetchConversations]);

  /* ── Lazy load relational data ── */
  const loadRelData = useCallback(async (tid: string) => {
    if (relLoaded || relLoading) return;
    setRelLoading(true);
    const [cRes, lRes, coRes, jRes] = await Promise.all([
      supabase.from('clients').select('id, full_name, email, phone').eq('tenant_id', tid).eq('status', 'active').order('full_name'),
      supabase.from('tenant_public_leads').select('id, name').eq('tenant_id', tid).order('name'),
      supabase.from('contracts').select('id, title, contract_number').eq('tenant_id', tid).order('title'),
      supabase.from('jobs').select('id, title').eq('tenant_id', tid).order('title'),
    ]);
    setClients((cRes.data as ClientFull[]) ?? []);
    setLeads((lRes.data as LeadOption[]) ?? []);
    setContracts((coRes.data as ContractOption[]) ?? []);
    setJobs((jRes.data as JobOption[]) ?? []);
    setRelLoaded(true);
    setRelLoading(false);
  }, [relLoaded, relLoading]);

  /* ── When a conversation with client_id is selected, resolve name ── */
  useEffect(() => {
    if (!selected) { setLinkedClient(null); return; }
    if (!selected.client_id) { setLinkedClient(null); return; }
    const found = clients.find((c) => c.id === selected.client_id);
    if (found) {
      setLinkedClient({ id: found.id, full_name: found.full_name });
    } else {
      // Fetch single client if not yet loaded
      supabase
        .from('clients')
        .select('id, full_name')
        .eq('id', selected.client_id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) setLinkedClient({ id: data.id, full_name: data.full_name });
        });
    }
  }, [selected, clients]);

  /* ── Filter change ── */
  const handleFilterChange = (f: 'all' | 'open' | 'closed') => {
    setFilter(f);
    setSelected(null);
    setLinkedClient(null);
    if (tenantId) fetchConversations(tenantId, f);
  };

  /* ── Select conversation ── */
  const handleSelect = async (conv: Conversation) => {
    setSelected(conv);
    setLoadingMsgs(true);
    const { data } = await supabase
      .from('ai_messages')
      .select('*')
      .eq('conversation_id', conv.id)
      .order('created_at', { ascending: true });
    setMessages((data as Message[]) ?? []);
    setLoadingMsgs(false);
  };

  /* ── Mark closed ── */
  const handleMarkClosed = async () => {
    if (!selected || !tenantId) return;
    await supabase
      .from('ai_conversations')
      .update({ status: 'closed', updated_at: new Date().toISOString() })
      .eq('id', selected.id);
    const updated = { ...selected, status: 'closed' };
    setSelected(updated);
    setConversations((prev) => prev.map((c) => (c.id === selected.id ? updated : c)));
  };

  /* ── Convert to Lead (guard: no duplicate) ── */
  const handleConvert = async () => {
    if (!selected || !tenantId) return;
    if (selected.lead_id) return; // already converted
    setConverting(true);

    const { data: lead, error } = await supabase
      .from('tenant_public_leads')
      .insert({
        tenant_id:  tenantId,
        name:       selected.visitor_name  ?? 'Unknown',
        email:      selected.visitor_email ?? null,
        phone:      selected.visitor_phone ?? null,
        event_type: selected.event_type    ?? null,
        event_date: selected.event_date    ?? null,
        message:    `Converted from AI chat — ${selected.id.slice(0, 8)}`,
        status:     'new',
        source:     'ai_assistant',
      })
      .select('id')
      .maybeSingle();

    if (error) {
      Swal.fire({ title: 'Error', text: error.message, icon: 'error' });
      setConverting(false);
      return;
    }

    if (lead) {
      await supabase
        .from('ai_conversations')
        .update({ lead_id: lead.id, updated_at: new Date().toISOString() })
        .eq('id', selected.id);

      const updated = { ...selected, lead_id: lead.id };
      setSelected(updated);
      setConversations((prev) => prev.map((c) => (c.id === selected.id ? updated : c)));

      createNotification({
        tenantId,
        type: 'success',
        title: 'Lead created from AI chat',
        message: `${selected.visitor_name ?? 'Visitor'} was converted to a new lead.`,
      });

      Swal.fire({ title: 'Lead created!', text: 'The visitor has been added to your leads.', icon: 'success', timer: 1600, showConfirmButton: false });
    }
    setConverting(false);
  };

  /* ── Link to Client ── */
  const handleOpenLinkModal = () => {
    if (!tenantId) return;
    loadRelData(tenantId);
    setLinkModalOpen(true);
  };

  const handleSelectClient = async (client: ClientFull) => {
    if (!selected || !tenantId) return;
    setLinkModalOpen(false);

    await supabase
      .from('ai_conversations')
      .update({ client_id: client.id, updated_at: new Date().toISOString() })
      .eq('id', selected.id);

    const updated = { ...selected, client_id: client.id };
    setSelected(updated);
    setConversations((prev) => prev.map((c) => (c.id === selected.id ? updated : c)));
    setLinkedClient({ id: client.id, full_name: client.full_name });

    Swal.fire({
      title: 'Client linked!',
      text: `Conversation linked to ${client.full_name}.`,
      icon: 'success',
      timer: 1500,
      showConfirmButton: false,
    });
  };

  /* ── Create Appointment ── */
  const handleOpenApptModal = () => {
    if (!tenantId) return;
    loadRelData(tenantId);
    setApptModalOpen(true);
  };

  const handleSaveAppointment = async (data: Appointment) => {
    if (!tenantId) return;

    const { id: _id, tenant_id: _t, created_at: _ca, updated_at: _ua, ...rest } = data;

    const { data: created, error } = await supabase
      .from('appointments')
      .insert({ ...rest, tenant_id: tenantId })
      .select()
      .maybeSingle();

    if (error) {
      Swal.fire({ title: 'Error', text: error.message, icon: 'error' });
      return;
    }

    if (created) {
      const createdAppt = created as Appointment;
      setApptModalOpen(false);

      createNotification({
        tenantId,
        type: 'info',
        title: 'Appointment created',
        message: `"${data.title}" booked from AI conversation.`,
      });

      createReminder({
        tenantId,
        reminderType: 'custom',
        entityType:   'appointment',
        entityId:     createdAppt.id,
        reminderDate: hoursBefore(data.start_at, 24),
        message:      'Appointment reminder',
      });

      Swal.fire({
        title: 'Appointment created!',
        text: `"${data.title}" has been scheduled.`,
        icon: 'success',
        timer: 1600,
        showConfirmButton: false,
      });
    }
  };

  /* ── Pre-fill for appointment modal ── */
  const getApptPrefillDate = () => {
    if (selected?.event_date) {
      return `${selected.event_date}T09:00`;
    }
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}T09:00`;
  };

  const apptPrefillAppointment = selected
    ? ({
        id: '',
        tenant_id: tenantId ?? '',
        client_id: selected.client_id ?? null,
        lead_id: selected.lead_id ?? null,
        contract_id: null,
        job_id: null,
        title: `Consultation — ${selected.visitor_name ?? 'Visitor'}`,
        appointment_type: 'Consultation',
        status: 'scheduled',
        start_at: getApptPrefillDate(),
        end_at: null,
        location: null,
        notes: selected.visitor_email
          ? `From AI chat. Email: ${selected.visitor_email}`
          : 'From AI chat widget.',
        show_in_portal: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      } as Appointment)
    : null;

  /* ── Stats ── */
  const totalOpen   = conversations.filter((c) => c.status === 'open').length;
  const totalClosed = conversations.filter((c) => c.status === 'closed').length;
  const totalLeads  = conversations.filter((c) => !!c.lead_id).length;

  return (
    <AdminLayout>
      <AdminHeader
        title="AI Conversations"
        subtitle="Chat history from your AI Sales Assistant widget"
      />

      <ModuleGate moduleId="ai_sales_assistant">
        <div className="px-4 md:px-8 py-4 md:py-6">

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Open',   value: totalOpen,   icon: 'ri-chat-3-line',          color: 'text-emerald-500', bg: 'bg-emerald-50 border-emerald-200' },
              { label: 'Closed', value: totalClosed, icon: 'ri-checkbox-circle-line', color: 'text-slate-500',   bg: 'bg-slate-50 border-slate-200' },
              { label: 'Leads',  value: totalLeads,  icon: 'ri-user-received-line',   color: 'text-[#C8A96E]',   bg: 'bg-[#C8A96E]/8 border-[#C8A96E]/25' },
            ].map((s) => (
              <div key={s.label} className={`rounded-xl border ${s.bg} px-4 py-3.5 flex items-center gap-3`}>
                <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-slate-200 shrink-0">
                  <i className={`${s.icon} ${s.color} text-base`} />
                </div>
                <div>
                  <p className="text-[#0f172a] text-lg font-bold leading-none">{s.value}</p>
                  <p className="text-slate-400 text-xs mt-0.5">{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Main panel */}
          <div
            className="bg-white rounded-xl border border-slate-200 overflow-hidden flex"
            style={{ height: '600px' }}
          >
            {/* Left: list */}
            <div className="w-72 shrink-0 border-r border-slate-200 flex flex-col">
              <ConversationListPanel
                conversations={conversations}
                loading={loading}
                filter={filter}
                onFilterChange={handleFilterChange}
                selectedId={selected?.id ?? null}
                onSelect={handleSelect}
              />
            </div>

            {/* Right: detail */}
            <div className="flex-1 min-w-0 flex flex-col">
              {selected ? (
                <ConversationDetail
                  conversation={selected}
                  messages={messages}
                  loadingMsgs={loadingMsgs}
                  linkedClient={linkedClient}
                  onMarkClosed={handleMarkClosed}
                  onConvert={handleConvert}
                  onLinkClient={handleOpenLinkModal}
                  onBookAppt={handleOpenApptModal}
                  converting={converting}
                />
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                  <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-slate-100 mb-4">
                    <i className="ri-robot-line text-slate-400 text-2xl" />
                  </div>
                  <p className="text-[#0f172a] font-semibold text-sm mb-1">Select a conversation</p>
                  <p className="text-slate-400 text-xs leading-relaxed max-w-xs">
                    Click any conversation on the left to read messages and take CRM actions.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModuleGate>

      {/* ── Link Client Modal ── */}
      {linkModalOpen && (
        <LinkClientModal
          clients={clients}
          loading={relLoading}
          onSelect={handleSelectClient}
          onClose={() => setLinkModalOpen(false)}
        />
      )}

      {/* ── Appointment Modal ── */}
      {apptModalOpen && apptPrefillAppointment && (
        <AppointmentModal
          appointment={apptPrefillAppointment}
          isNew
          clients={clients as ClientOption[]}
          leads={leads}
          contracts={contracts}
          jobs={jobs}
          prefillStartAt={getApptPrefillDate()}
          onClose={() => setApptModalOpen(false)}
          onSave={handleSaveAppointment}
        />
      )}
    </AdminLayout>
  );
}